// client/src/NewEvent.js
import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { useHistory } from 'react-router-dom';
import { API_BASE } from './api';
import { BACKGROUND_CHOICES } from './eventBackground';
import { formatDate } from './formatDate';
import { getApproximateCity } from './approximateCity';
import InviteEmailsField, { addInviteEmails } from './InviteEmailsField';
import { rememberPlace, searchPlaces } from './placeSearch';
import BannerPicker from './BannerPicker';
import RichTextEditor from './RichTextEditor';
import { clearEventDraft, readEventDraft } from './eventDraft';
import { uploadImage } from './uploadImage';
import { describeParsedEvent, parseEventText } from './parseEventText';
import { navState } from './navState';
import { createBotChecker, isBotCheckRejection } from './botCheck';
import Honeypot from './Honeypot';

const MAPBOX_TOKEN =
  process.env.REACT_APP_MAPBOX_TOKEN;

function useDebouncedValue(value, delay = 250) {
  const [debouncedValue, setDebouncedValue] =
    useState(value);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => clearTimeout(timer);
  }, [value, delay]);

  return debouncedValue;
}

// Date / time box that looks like the other fields: grey placeholder text
// ("Date", "Time") until something is picked. Native date/time inputs can't
// show placeholder text, so it's laid over the input while it's empty, and
// the browser's own "dd.mm.yyyy" / "--:--" is hidden until then.
function PickerField({ type, placeholder, value, onChange, label, required = false, className = '', note = '', onClear, clearLabel = 'Clear' }) {
  const empty = !value;
  return (
    <div className={`native-picker-button ${className}`.trim()}>
      <input
        type={type}
        className={`native-picker-input${empty ? ' is-empty' : ''}`}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        step={type === 'time' ? '300' : undefined}
        aria-label={label}
        required={required}
      />
      {empty && (
        <span className="native-picker-placeholder" aria-hidden="true">{placeholder}</span>
      )}
      {!empty && note && (
        <span className="native-picker-note">{note}</span>
      )}
      {!empty && onClear && (
        <button type="button" className="end-time-clear" onClick={onClear} aria-label={clearLabel}>
          ×
        </button>
      )}
    </div>
  );
}

function LocationAutocomplete({
  value,
  onInputChange,
  onPick,
  placeholder = 'Location',
  minChars = 3,
}) {
  const [open, setOpen] =
    useState(false);

  const [loading, setLoading] =
    useState(false);

  const [results, setResults] =
    useState([]);

  const [error, setError] =
    useState(null);

  const [refining, setRefining] =
    useState(false);

  const debouncedValue =
    useDebouncedValue(value, 250);

  // Still typing (waiting for the pause before searching) counts as searching,
  // so "No results" doesn't flash up mid-word.
  const searching = loading || value !== debouncedValue;

  const wrapperRef = useRef(null);

  useEffect(() => {
    if (!open) {
      return;
    }

    setError(null);
    setResults([]);
    setLoading(false);

    if (
      !debouncedValue ||
      debouncedValue.trim().length <
        minChars
    ) {
      return;
    }

    // Venues, businesses, addresses and cities, typos forgiven (placeSearch.js).
    // A quick first list can be replaced by a better one a moment later.
    const controller = new AbortController();
    setLoading(true);
    setRefining(false);

    searchPlaces(debouncedValue, {
      signal: controller.signal,
      onResults: (places, { pending }) => {
        setResults(places);
        setLoading(false);
        setRefining(pending);
      },
    });

    return () => {
      controller.abort();
      setRefining(false);
    };
  }, [
    debouncedValue,
    open,
    minChars,
  ]);

  useEffect(() => {
    const handleDocumentClick = (
      event
    ) => {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(
          event.target
        )
      ) {
        setOpen(false);
      }
    };

    document.addEventListener(
      'mousedown',
      handleDocumentClick
    );

    document.addEventListener(
      'touchstart',
      handleDocumentClick
    );

    return () => {
      document.removeEventListener(
        'mousedown',
        handleDocumentClick
      );

      document.removeEventListener(
        'touchstart',
        handleDocumentClick
      );
    };
  }, []);

  const showPopover =
    open &&
    (
      error ||
      searching ||
      results.length > 0 ||
      (
        value &&
        value.trim().length >=
          minChars
      )
    );

  return (
    <div
      ref={wrapperRef}
      style={{
        position: 'relative',
      }}
    >
      <input
        type="text"
        className="newevent-field"
        value={value}
        onChange={(event) => {
          onInputChange(
            event.target.value
          );
          setOpen(true);
        }}
        onFocus={() =>
          setOpen(true)
        }
        placeholder={placeholder}
        aria-label="Location"
        name="location"
        autoComplete="off"
        style={
          autocompleteStyles.input
        }
      />

      {showPopover && (
        <div
          style={
            autocompleteStyles.popover
          }
        >
          {error === 'network' && (
            <div
              style={{
                ...autocompleteStyles.item,
                color: '#a00',
              }}
            >
              Network error.
            </div>
          )}

          {!error && searching && results.length === 0 && (
            <div
              style={{
                ...autocompleteStyles.item,
                color: '#666',
              }}
            >
              Searching…
            </div>
          )}

          {!error &&
            results.map(
              (result) => (
                <button
                  type="button"
                  key={result.id}
                  style={
                    autocompleteStyles.item
                  }
                  onClick={() => {
                    // Keeps the place's own city and country with it.
                    onPick(result);
                    rememberPlace(result);

                    setOpen(false);
                  }}
                >
                  <span className="place-result-name">{result.primary}</span>
                  {result.secondary && (
                    <span className="place-result-address">{result.secondary}</span>
                  )}
                </button>
              )
            )}

          {!error && refining && results.length > 0 && (
            <div
              style={{
                ...autocompleteStyles.item,
                color: '#9a7f69',
                fontSize: 12,
              }}
            >
              Finding more places…
            </div>
          )}

          {!error &&
            !searching &&
            !refining &&
            results.length === 0 &&
            value.trim().length >=
              minChars && (
              <div
                style={{
                  ...autocompleteStyles.item,
                  color: '#666',
                }}
              >
                No results
              </div>
            )}
        </div>
      )}
    </div>
  );
}

const autocompleteStyles = {
  input: {
    display: 'block',
    width: '100%',
    minWidth: 0,
    maxWidth: '100%',
    boxSizing: 'border-box',
    padding: '14px 16px',
    borderRadius: 5,
    border: '1px solid #ccc',
    marginBottom: 14,
    fontSize: 16,
    outline: 'none',
    background: '#fff',
    color: '#000',
  },

  popover: {
    position: 'absolute',
    left: 0,
    right: 0,
    top: 'calc(100% + 6px)',
    background: '#fff',
    border: '1px solid #000',
    borderRadius: 6,
    boxShadow:
      '0 12px 28px rgba(0,0,0,.14)',
    overflow: 'hidden',
    zIndex: 50,
  },

  item: {
    display: 'block',
    width: '100%',
    textAlign: 'left',
    padding: '10px 14px',
    background: '#fff',
    border: 'none',
    borderBottom:
      '1px solid #eee',
    cursor: 'pointer',
    fontSize: 14,
    color: '#000',
  },
};

export default function NewEvent({
  addEvent,
}) {
  const history = useHistory();

  // Slides up when opened from within the site; on a fresh load the whole
  // page slides up instead (navState.js).
  const [slideInClass] = useState(() => (navState.firstPage ? '' : ' slide-in'));

  // Bot check: start solving as soon as the page opens (botCheck.js).
  const botCheckerRef = useRef(null);
  const honeypotRef = useRef(null);
  useEffect(() => {
    botCheckerRef.current = createBotChecker();
    botCheckerRef.current.start();
  }, []);

  // Filled in from a sentence typed into the home search bar, if any.
  // Cleared after the first render (not inside the initialiser, which React
  // can run twice).
  const [draft] = useState(readEventDraft);
  useEffect(() => {
    clearEventDraft();
  }, []);

  const [title, setTitle] =
    useState(draft?.title || '');

  const [
    locationInput,
    setLocationInput,
  ] = useState(draft?.location || '');

  const [
    pickedPlace,
    setPickedPlace,
  ] = useState(null);

  const [dateStr, setDateStr] =
    useState(draft?.date || '');

  const [timeStr, setTimeStr] =
    useState(draft?.time || '');

  // Optional end time, same day, or the next day if it's before the start.
  const [endTimeStr, setEndTimeStr] =
    useState(draft?.endTime || '');

  // "Describe it" box at the top: a sentence that fills in the fields.
  const [describeText, setDescribeText] = useState('');
  const [filledFromText, setFilledFromText] = useState(false);
  const describedEvent = useMemo(() => {
    const parsed = parseEventText(describeText);
    return parsed.title || parsed.date || parsed.time ? parsed : null;
  }, [describeText]);

  const applyDescription = () => {
    if (!describedEvent) return;
    const { title: newTitle, date, time, endTime, location } = describedEvent;
    if (newTitle) setTitle(newTitle);
    if (date) setDateStr(date);
    if (time) setTimeStr(time);
    if (endTime) setEndTimeStr(endTime);
    if (location) {
      setLocationInput(location);
      setPickedPlace(null);
    }
    setDescribeText('');
    setFilledFromText(true);
  };

  const [
    description,
    setDescription,
  ] = useState('');

  const [banner, setBanner] =
    useState('');

  const [
    uploadingImage,
    setUploadingImage,
  ] = useState(false);

  const [
    isSubmitting,
    setIsSubmitting,
  ] = useState(false);

  const [isPublic, setIsPublic] =
    useState(true);

  const [inviteEmails, setInviteEmails] = useState([]);
  const [inviteDraft, setInviteDraft] = useState('');

  const [
    backgroundColor,
    setBackgroundColor,
  ] = useState(BACKGROUND_CHOICES[0].value);

  const [bannerError, setBannerError] = useState('');

  // Uploads a picture to Cloudinary and returns its address. Used for the
  // event banner and for images inside the description.
  // Upload a picture (event picture or one in the description).
  const uploadImageFile = (file) => uploadImage(file);

  // Banner picture: upload and keep its address.
  const uploadBannerFile = async (file) => {
    if (!file) {
      return;
    }

    setBannerError('');
    try {
      setUploadingImage(true);
      setBanner(await uploadImageFile(file));
    } catch (error) {
      setBannerError(error.message);
    } finally {
      setUploadingImage(false);
    }
  };

  const combinedDate =
    useMemo(() => {
      if (
        !dateStr ||
        !timeStr
      ) {
        return null;
      }

      const date = new Date(
        `${dateStr}T${timeStr}`
      );

      if (
        Number.isNaN(
          date.getTime()
        )
      ) {
        return null;
      }

      return date;
    }, [dateStr, timeStr]);

  const endsAt = useMemo(() => {
    if (!combinedDate || !endTimeStr) return null;
    const end = new Date(`${dateStr}T${endTimeStr}`);
    if (Number.isNaN(end.getTime())) return null;
    if (end <= combinedDate) end.setDate(end.getDate() + 1);
    return end;
  }, [combinedDate, dateStr, endTimeStr]);

  const endsNextDay = Boolean(
    endsAt && combinedDate && endsAt.getDate() !== combinedDate.getDate()
  );

  const locationText =
    (pickedPlace?.label || locationInput).trim();

  // Location isn't required here: if it's empty, submitting offers the
  // person's current city instead (only used if they confirm it).
  const missingFields = [
    !title.trim() && 'a title',
    !dateStr && 'a date',
    !timeStr && 'a time',
  ].filter(Boolean);

  // null | { status: 'locating' } | { status: 'found', place } | { status: 'error', message }
  const [cityPrompt, setCityPrompt] = useState(null);

  const focusLocationField = () => {
    const field = document.querySelector('.create-event-form input[name="location"]');
    if (field) {
      field.scrollIntoView({ block: 'center' });
      field.focus();
    }
  };

  const offerCurrentCity = async () => {
    setCityPrompt({ status: 'locating' });
    try {
      const place = await getApproximateCity(MAPBOX_TOKEN);
      setCityPrompt({ status: 'found', place });
    } catch (error) {
      setCityPrompt({ status: 'error', message: error.message });
    }
  };

  // Bring the question into view: on phones it sits far above the Create button.
  const cityPromptRef = useRef(null);
  const cityPromptStatus = cityPrompt ? cityPrompt.status : null;
  useEffect(() => {
    if (cityPromptStatus && cityPromptRef.current) {
      cityPromptRef.current.scrollIntoView({ block: 'center', behavior: 'smooth' });
    }
  }, [cityPromptStatus]);

  const createWithoutLocation = () => {
    setCityPrompt(null);
    if (canSubmit && combinedDate) submitEvent('', null);
  };

  const canSubmit =
    missingFields.length === 0 &&
    Boolean(combinedDate) &&
    !uploadingImage &&
    !isSubmitting;

  const handleSubmit = async (
    event
  ) => {
    event.preventDefault();

    if (
      !canSubmit ||
      !combinedDate
    ) {
      return;
    }

    if (!locationText) {
      offerCurrentCity();
      return;
    }

    submitEvent(locationText, pickedPlace);
  };

  const acceptOfferedCity = () => {
    const { place } = cityPrompt;
    setLocationInput(place.label);
    setPickedPlace(place);
    setCityPrompt(null);
    submitEvent(place.label, place);
  };

  const submitEvent = async (locationValue, place) => {
    const authUser =
      JSON.parse(
        localStorage.getItem(
          'auth_user'
        ) || '{}'
      );

    const userEmail =
      authUser?.email || '';
    const authToken = localStorage.getItem('auth_token') || '';

    const payload = {
      title:
        title.trim() ||
        'Untitled Event',

      date:
        formatDate(combinedDate),

      time:
        combinedDate.toLocaleTimeString(
          [],
          {
            hour: '2-digit',
            minute: '2-digit',
          }
        ),

      // Unambiguous start time; date/time above are locale display strings.
      startsAt:
        combinedDate.toISOString(),

      endsAt: endsAt ? endsAt.toISOString() : undefined,

      location:
        locationValue,

      // City and country as Mapbox reports them (empty for typed locations),
      // so cards and the Locations list don't have to guess from the address.
      city: place?.city || '',
      country: place?.country || '',

      locationGeo: place
        ? {
            lng:
              place.center?.[0],

            lat:
              place.center?.[1],

            bbox:
              place.bbox ||
              null,
          }
        : null,

      description,

      // Includes an address that was typed but not confirmed with Enter.
      inviteEmails: addInviteEmails(inviteEmails, inviteDraft).emails,

      bannerImage: banner,

      isPublic,

      public: isPublic,

      backgroundColor:
        backgroundColor ||
        '#FF7A00',

      hostEmail:
        userEmail ||
        'guest@frolik.world',

      creatorEmail:
        userEmail,

    };

    try {
      setIsSubmitting(true);

      // Bot check (solved in the background while the form was filled in) and
      // the hidden "website" field. One retry with a fresh check if the old
      // one expired because the page was open a long time.
      const send = async () => {
        const response = await fetch(`${API_BASE}/api/events`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${authToken}`,
          },
          body: JSON.stringify({
            ...payload,
            website: honeypotRef.current ? honeypotRef.current.value : '',
            botCheck: await botCheckerRef.current.take(),
          }),
        });
        return { response, data: await response.json() };
      };

      let { response, data } = await send();
      if (isBotCheckRejection(response.status, data)) ({ response, data } = await send());

      if (!response.ok) {
        throw new Error(
          data.error ||
            'Failed to create event'
        );
      }

      addEvent?.({
        id: data.slug,
        ...payload,

        banner,

        dateTime:
          combinedDate.toISOString(),

        createdBy:
          userEmail,
      });

      history.push(
        `/event/${data.slug}`,
        { justCreated: true }
      );
    } catch (error) {
      console.error(error);

      alert(
        error.message ||
          'Failed to create event'
      );

      // Only reset on failure; on success the page has already navigated away.
      setIsSubmitting(false);
    }
  };

  return (
    <div className="create-event-page">
    <form
      className={`create-event-form${slideInClass}`}
      onSubmit={handleSubmit}
      style={styles.form}
    >
      <style>{`
        .create-event-page {
          width: 100%;
          padding: 1px 16px 32px;
          box-sizing: border-box;
          color: #512f14;
          font-family: Arial, Helvetica, sans-serif !important;
        }

        .create-event-form {
          max-width: 760px !important;
          margin: 28px auto 0 !important;
          padding: 0 0 30px !important;
          overflow: visible !important;
          /* Shiny orange-white-orange border (like the share card): the border is
             transparent and the last background layer paints the gradient under it,
             while the brushed metal layers are clipped to the inside. */
          border: 1px solid transparent;
          border-radius: 6px;
          /* Brushed metal: diagonal sheen over horizontally stretched noise
             (irregular brushing streaks) on a warm silver base. */
          background:
            linear-gradient(115deg, rgba(255,255,255,0) 22%, rgba(255,255,255,0.45) 42%, rgba(255,255,255,0) 60%) padding-box,
            url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='400' height='400'><filter id='b'><feTurbulence type='fractalNoise' baseFrequency='0.003 0.9' numOctaves='3' seed='7' stitchTiles='stitch'/><feColorMatrix values='0 0 0 0 0.58  0 0 0 0 0.42  0 0 0 0 0.30  0 0 0 0.32 0'/></filter><rect width='100%' height='100%' filter='url(%23b)'/></svg>") padding-box,
            linear-gradient(180deg, #faf5f1 0%, #eddfd3 45%, #f6eee7 70%, #e8d9cc 100%) padding-box,
            linear-gradient(135deg, #9b551b 0%, #ff8b2b 30%, #ffffff 50%, #ff8b2b 70%, #9b551b 100%) border-box;
          box-shadow: 0 12px 30px rgba(134,69,16,0.3);
        }

        /* Top of the card: silver like the rest, holding the "describe your
           event" box that fills in the form. */
        .create-event-header {
          position: relative;
          margin: 0 0 18px;
          padding: 22px 28px 18px;
          border-bottom: 1px solid #e5c9b2;
          background: transparent;
          color: #512f14;
        }

        /* Kept for screen readers; the describe box takes its place on screen. */
        .create-event-title {
          position: absolute !important;
          width: 1px;
          height: 1px;
          overflow: hidden;
          clip: rect(0 0 0 0);
          white-space: nowrap;
        }

        .create-event-form > .newevent-field,
        .create-event-form > div,
        .create-event-form > label,
        .create-event-form > button,
        .create-event-form > img {
          margin-left: 28px;
          margin-right: 28px;
          max-width: calc(100% - 56px);
          box-sizing: border-box;
        }

        .create-event-form > h1 {
          display: none;
        }

        .create-event-form .newevent-field {
          margin-bottom: 13px !important;
          padding: 12px 14px !important;
          border: 1px solid #e5c9b2 !important;
          border-radius: 3px !important;
          outline: none;
          background: linear-gradient(180deg, #faf1e9 0%, #fff 12%, #fff 100%) !important;
          box-shadow: inset 0 1px 3px rgba(134,69,16,0.10), 0 1px 0 #fff;
          color: #512f14 !important;
          font-size: 15px !important;
          line-height: 1.35 !important;
          transition: border-color 0.15s ease, box-shadow 0.15s ease;
        }

        .create-event-form > input.newevent-field,
        .create-event-form > textarea.newevent-field {
          width: calc(100% - 56px) !important;
        }

        .create-event-form > div .newevent-field {
          width: 100% !important;
        }

        .create-event-form .newevent-field:focus {
          border-color: #ff7708 !important;
          box-shadow: 0 0 0 3px rgba(255,119,8,0.22), inset 0 1px 3px rgba(134,69,16,0.08);
        }

        .newevent-field::placeholder {
          color: #9a7f69 !important;
          opacity: 1 !important;
        }

        .create-event-form > div[style] {
          color: #7e614a;
        }

        .create-event-form .when-heading {
          color: #98551f;
          font-size: 10px !important;
          letter-spacing: 0.14em !important;
        }

        .create-event-form .when-section {
          margin-top: 7px;
        }

        .create-event-form .native-picker-button {
          border: 1px solid #e5c9b2;
          border-radius: 3.5px;
          background: linear-gradient(180deg, #faf1e9 0%, #fff 18%, #fff 100%);
          box-shadow: inset 0 1px 3px rgba(134,69,16,0.08), 0 1px 0 #fff;
        }


        .create-event-form .native-picker-input {
          color: #512f14;
          font-size: 15px !important;
        }

        /* Empty: hide the browser's "dd.mm.yyyy" / "--:--" behind the placeholder. */
        .create-event-form .native-picker-input.is-empty,
        .create-event-form .native-picker-input.is-empty::-webkit-datetime-edit {
          color: transparent !important;
        }

        /* While focused (e.g. typing a date on a keyboard), show the real field. */
        .create-event-form .native-picker-input.is-empty:focus,
        .create-event-form .native-picker-input.is-empty:focus::-webkit-datetime-edit {
          color: #512f14 !important;
        }

        .create-event-form .native-picker-input:focus + .native-picker-placeholder {
          display: none;
        }

        .create-event-form .native-picker-placeholder {
          position: absolute;
          top: 50%;
          left: 14px;
          transform: translateY(-50%);
          color: #9a7f69;
          font-size: 15px !important;
          pointer-events: none;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          max-width: calc(100% - 28px);
        }

        .create-event-form .native-picker-note {
          position: absolute;
          top: 50%;
          right: 42px;
          transform: translateY(-50%);
          color: #9a7f69;
          font-size: 11px !important;
          pointer-events: none;
        }


        .create-event-form .create-event-bg-options {
          padding-top: 14px;
          border-top: 1px solid #eedac9;
        }

        .create-event-form .create-event-bg-options > div:first-child,
        .create-event-form .label {
          color: #98551f !important;
          font-size: 12px !important;
          font-weight: 700;
        }

        .create-event-form input[type="file"] {
          color: #7e614a;
          font-size: 12px !important;
        }

        .create-event-form input[type="radio"],
        .create-event-form input[type="checkbox"] {
          accent-color: #f56e00;
        }

        .create-event-form .hint {
          font-size: 12px !important;
          line-height: 1.45 !important;
        }

        .create-event-form > label {
          padding: 12px 14px;
          border: 1px solid #eedac9;
          border-radius: 3px;
          background: rgba(255,255,255,0.55);
          color: #512f14;
          font-size: 12px !important;
        }

        .create-event-visibility {
          display: grid;
          grid-template-columns: repeat(2, minmax(0, 1fr));
          gap: 10px;
          max-width: calc(100% - 56px);
          margin-top: 14px;
          margin-right: 28px;
          margin-left: 28px;
          padding: 0;
          border: 0;
        }

        .create-event-visibility legend {
          margin-bottom: 8px;
          color: #98551f;
          font-size: 12px !important;
          font-weight: 700;
        }

        .create-event-visibility-option {
          display: flex;
          align-items: flex-start;
          gap: 9px;
          min-width: 0;
          padding: 12px;
          border: 1px solid #e5c9b2;
          border-radius: 3.5px;
          background: linear-gradient(180deg, #fff 0%, #fcf7f3 100%);
          color: #512f14;
          cursor: pointer;
          box-shadow: 0 1px 2px rgba(134,69,16,0.08);
        }

        .create-event-visibility-option:has(input:checked) {
          border-color: #ff7708;
          background: linear-gradient(180deg, #fff8f3 0%, #ffeee0 100%);
          box-shadow: 0 0 0 2px rgba(245,110,0,0.15);
        }

        .create-event-visibility-option input {
          flex: 0 0 auto;
          margin: 2px 0 0;
        }

        .create-event-visibility-name {
          display: block;
          margin-bottom: 4px;
          color: #512f14;
          font-size: 13px !important;
          font-weight: 700;
        }

        .create-event-visibility-description {
          display: block;
          color: #7e614a;
          font-size: 11px !important;
          font-weight: 400;
          line-height: 1.4 !important;
        }

        .create-event-form > button[type="submit"] {
          width: calc(100% - 56px) !important;
          max-width: calc(100% - 56px) !important;
          height: 48px;
          margin-top: 20px !important;
          border: 1px solid #e66700 !important;
          border-radius: 3.5px !important;
          background: linear-gradient(180deg, #fdc18f 0%, #ff7e14 48%, #e66700 100%) !important;
          box-shadow: inset 0 1px 0 rgba(255,255,255,0.6), inset 0 -2px 0 rgba(146,71,9,0.2), 0 2px 3px rgba(123,62,12,0.2);
          color: #fff !important;
          font-size: 14px !important;
          font-weight: 700;
          text-shadow: 0 1px 1px rgba(124,59,6,0.45);
          transition: filter 0.15s ease, transform 0.15s ease;
        }

        .create-event-form > button[type="submit"]:not(:disabled):hover {
          filter: brightness(1.04);
          transform: translateY(-1px);
        }

        .create-event-form > button[type="submit"]:disabled {
          cursor: not-allowed;
          filter: grayscale(0.25);
        }

        .create-event-form .preview {
          border: 1px solid #e5c9b2;
          box-shadow: 0 2px 8px rgba(134,69,16,0.12);
        }

        @media (max-width: 600px) {
          .create-event-page {
            padding: 1px 12px 24px;
          }

          .create-event-form {
            width: 100% !important;
            max-width: 100% !important;
            margin-top: 14px !important;
            padding-bottom: 22px !important;
            border-radius: 5px;
          }

          .create-event-header {
            padding: 18px 16px 16px;
            margin-bottom: 18px;
          }

          .create-event-visibility {
            max-width: calc(100% - 32px);
            margin-right: 16px;
            margin-left: 16px;
          }


          .create-event-form > .newevent-field,
          .create-event-form > div,
          .create-event-form > label,
          .create-event-form > button,
          .create-event-form > img {
            margin-left: 16px;
            margin-right: 16px;
            max-width: calc(100% - 32px);
          }

          .create-event-form > input.newevent-field,
          .create-event-form > textarea.newevent-field,
          .create-event-form > button[type="submit"] {
            width: calc(100% - 32px) !important;
            max-width: calc(100% - 32px) !important;
          }

          .create-event-form > textarea.newevent-field {
            height: 132px !important;
            min-height: 100px !important;
          }

          .create-event-form .newevent-field {
            font-size: 16px !important;
          }

          .create-event-form .native-picker-input {
            padding: 0 36px 0 14px;
            font-size: 15px !important;
          }

          .create-event-form .create-event-upload {
            display: flex;
            flex-direction: column;
            align-items: flex-start;
            gap: 8px;
          }

          .create-event-form .create-event-upload input[type="file"] {
            width: 100%;
            max-width: 100%;
          }

          .create-event-visibility {
            grid-template-columns: 1fr;
            gap: 8px;
          }

          .create-event-visibility-option {
            padding: 12px;
          }
        }

        .when-section {
          width: 100%;
          min-width: 0;
          max-width: 100%;
          margin: 4px 0 18px;
          box-sizing: border-box;
        }

        .when-heading {
          margin: 0 0 9px 2px;
          font-size: 12px;
          font-weight: 700;
          letter-spacing: 0.12em;
          color: rgba(0, 0, 0, 0.55);
        }

        .when-buttons {
          display: grid;
          grid-template-columns:
            minmax(0, 1fr)
            minmax(0, 1fr);
          gap: 12px;
          width: 100%;
          min-width: 0;
          max-width: 100%;
          box-sizing: border-box;
        }

        .native-picker-button {
          position: relative;
          display: flex;
          align-items: center;
          width: 100%;
          min-width: 0;
          height: 48px;
          overflow: hidden;
          box-sizing: border-box;
          border: 1px solid #ccc;
          border-radius: 7px;
          background: #fff;
        }




        .native-picker-input {
          display: block;
          width: 100%;
          min-width: 0;
          max-width: 100%;
          height: 100%;
          margin: 0;
          padding: 0 40px 0 14px;
          box-sizing: border-box;
          border: 0;
          border-radius: 7px;
          outline: none;
          background: transparent;
          color: #000;
          font-family:
            Helvetica,
            Arial,
            sans-serif;
          font-size: 15px;
          cursor: pointer;
          color-scheme: light;
          -webkit-appearance: none;
          appearance: none;
        }

        /* Phones centre the picked value and drop it low; keep it left and
           in the middle, like the text in the other fields. */
        .native-picker-input::-webkit-date-and-time-value {
          min-width: 0;
          margin: 0;
          text-align: left;
        }

        .native-picker-input {
          display: flex !important;
          align-items: center;
          text-align: left;
          line-height: normal;
        }

        .native-picker-input::-webkit-calendar-picker-indicator {
          position: absolute;
          inset: 0;
          width: 100%;
          height: 100%;
          margin: 0;
          padding: 0;
          opacity: 0;
          cursor: pointer;
        }

        .native-picker-input::-webkit-datetime-edit {
          min-width: 0;
          padding: 0;
        }

        .native-picker-input::-webkit-datetime-edit-fields-wrapper {
          padding: 0;
        }

        .native-picker-input:focus {
          outline: none;
        }

        .native-picker-button:focus-within {
          border-color: #000;
          box-shadow:
            0 0 0 1px #000;
        }

        @media (max-width: 420px) {
          .when-buttons {
            gap: 10px;
          }

          .native-picker-button {
            height: 48px;
          }

          .native-picker-input {
            padding: 0 36px 0 12px;
          }
        }

        @media (max-width: 330px) {
          .when-buttons {
            grid-template-columns: 1fr;
          }

          .when-buttons .native-picker-button.is-date {
            grid-column: auto;
          }
        }

        /* Date on its own row; Start and End side by side underneath. */
        .native-picker-button.is-date {
          grid-column: 1 / -1;
        }

        .end-time-clear {
          position: absolute;
          top: 50%;
          right: 8px;
          transform: translateY(-50%);
          width: 28px;
          height: 28px;
          border: 0;
          border-radius: 50%;
          background: rgba(255, 119, 8, 0.12);
          color: #98551f;
          font-size: 18px !important;
          line-height: 1 !important;
          cursor: pointer;
        }

        @media (max-width: 360px) {
          .create-event-page {
            padding-right: 8px;
            padding-left: 8px;
          }

          .create-event-form > div,
          .create-event-form > label,
          .create-event-form > img {
            margin-left: 12px;
            margin-right: 12px;
            max-width: calc(100% - 24px);
          }

          .create-event-form > input.newevent-field,
          .create-event-form > textarea.newevent-field,
          .create-event-form > button[type="submit"] {
            width: calc(100% - 24px) !important;
            max-width: calc(100% - 24px) !important;
            margin-left: 12px;
            margin-right: 12px;
          }

          .create-event-header {
            padding-right: 13px;
            padding-left: 13px;
          }

          .create-event-visibility {
            max-width: calc(100% - 24px);
            margin-right: 12px;
            margin-left: 12px;
          }

          .when-buttons {
            grid-template-columns: 1fr;
          }

          .native-picker-button {
            height: 58px;
          }
        }
      `}</style>

      <Honeypot ref={honeypotRef} />

      <header className="create-event-header">
        <h1 className="create-event-title">
          Create an event
        </h1>

        {/* Describe the event in a sentence; Enter or "Fill in" fills the form. */}
        <input
          className="create-nl-input"
          type="text"
          value={describeText}
          onChange={(event) => setDescribeText(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              applyDescription();
            }
          }}
          placeholder="Describe it: this saturday bike ride 11am to 4pm"
          aria-label="Describe your event in a sentence to fill in the form"
          enterKeyHint="done"
          autoComplete="off"
        />

        {describedEvent && (
          <button
            type="button"
            className="create-nl-fill"
            onMouseDown={(event) => event.preventDefault()}
            onClick={applyDescription}
          >
            <span className="create-nl-label">Fill in</span>
            <strong>{describedEvent.title || 'Untitled'}</strong>
            {describeParsedEvent(describedEvent) && <span>{describeParsedEvent(describedEvent)}</span>}
          </button>
        )}
      </header>

      {(draft || filledFromText) && (
        <p className="create-event-draft-note" role="status">
          Filled in from what you typed. Check the details, then add a picture, a description and a
          background, choose public or private, and press Create Event.
        </p>
      )}

      <input
        className="newevent-field"
        style={styles.input}
        placeholder="Title"
        value={title}
        onChange={(event) => {
          setTitle(
            event.target.value
          );
        }}
        required
      />

      <LocationAutocomplete
        value={locationInput}
        onInputChange={(value) => {
          setCityPrompt(null);
          setLocationInput(value);
          setPickedPlace(null);
        }}
        onPick={(place) => {
          setLocationInput(
            place.label
          );
          setPickedPlace(place);
        }}
        placeholder="Location (optional)"
      />

      {cityPrompt && (
        <div className="city-prompt" role="status" ref={cityPromptRef}>
          {cityPrompt.status === 'locating' && (
            <span>No location entered. Finding your city…</span>
          )}

          {cityPrompt.status === 'found' && (
            <span>
              No location entered. Use <strong>{cityPrompt.place.label}</strong> as the location?
            </span>
          )}

          {cityPrompt.status === 'error' && (
            <span>No location entered, and your city couldn’t be found. Go ahead without one, or type it.</span>
          )}

          {/* Location is optional: there's always a way to go ahead without one. */}
          <div className="city-prompt-actions">
            {cityPrompt.status === 'found' && (
              <button type="button" onClick={acceptOfferedCity}>
                Use {cityPrompt.place.label.split(',')[0]}
              </button>
            )}
            <button
              type="button"
              className={cityPrompt.status === 'found' ? 'is-secondary' : ''}
              onClick={createWithoutLocation}
              disabled={isSubmitting}
            >
              Create without a location
            </button>
            <button
              type="button"
              className="is-secondary"
              onClick={() => {
                setCityPrompt(null);
                focusLocationField();
              }}
            >
              I’ll type it
            </button>
          </div>
        </div>
      )}

      <div className="when-section">
        <div className="when-buttons">
          <PickerField
            type="date"
            placeholder="Date"
            value={dateStr}
            onChange={setDateStr}
            label="Event date"
            className="is-date"
            required
          />

          <PickerField
            type="time"
            placeholder="Start"
            value={timeStr}
            onChange={setTimeStr}
            label="Event start time"
            required
          />

          <PickerField
            type="time"
            placeholder="End (optional)"
            value={endTimeStr}
            onChange={setEndTimeStr}
            label="Event end time (optional)"
            className="is-end-time"
            note={endsNextDay ? 'next day' : ''}
            onClear={() => setEndTimeStr('')}
            clearLabel="Remove end time"
          />
        </div>
      </div>

      <RichTextEditor
        placeholder="Description"
        onChange={setDescription}
        onUploadImage={uploadImageFile}
      />

      <InviteEmailsField
        emails={inviteEmails}
        onChange={setInviteEmails}
        draft={inviteDraft}
        onDraftChange={setInviteDraft}
        // Private events go live straight away, so their invites do too.
        sendsRightAway={!isPublic}
      />

      <BannerPicker
        banner={banner}
        uploading={uploadingImage}
        error={bannerError}
        onFile={uploadBannerFile}
        onRemove={() => {
          setBanner('');
          setBannerError('');
        }}
      />

      <div
        className="create-event-bg-options"
        style={{
          marginTop: 12,
        }}
      >
        <div style={styles.label}>
          Choose background
          colour:
        </div>

        {/* Big named tiles (easy to tap on phones); the radio inputs stay
            for keyboards and screen readers. */}
        <div className="bg-swatches" role="radiogroup" aria-label="Background colour">
          {BACKGROUND_CHOICES.map(({ label, value: colour }) => {
            const selected = backgroundColor === colour;
            return (
              <label
                key={colour}
                className={`bg-swatch${selected ? ' is-selected' : ''}`}
              >
                <input
                  type="radio"
                  name="backgroundColor"
                  aria-label={label}
                  value={colour}
                  checked={selected}
                  onChange={(event) => setBackgroundColor(event.target.value)}
                />
                <span className="bg-swatch-colour" style={{ background: colour }}>
                  {selected && <span className="bg-swatch-tick" aria-hidden="true">✓</span>}
                </span>
                <span className="bg-swatch-name">{label.replace(/ gradient$/i, '')}</span>
              </label>
            );
          })}
        </div>
      </div>

      <fieldset className="create-event-visibility">
        <legend>Who can access this event?</legend>

        <label className="create-event-visibility-option">
          <input
            type="radio"
            name="eventVisibility"
            value="private"
            checked={!isPublic}
            onChange={() => setIsPublic(false)}
          />
          <span>
            <span className="create-event-visibility-name">Private</span>
            <span className="create-event-visibility-description">
              Only you and people you share this link with can access the event. DO NOT lose the link.
            </span>
          </span>
        </label>

        <label className="create-event-visibility-option">
          <input
            type="radio"
            name="eventVisibility"
            value="public"
            checked={isPublic}
            onChange={() => setIsPublic(true)}
          />
          <span>
            <span className="create-event-visibility-name">Public</span>
            <span className="create-event-visibility-description">
              You and people you share this link with can access the event.{' '}
              <strong>It will also be listed on the front page.</strong>
            </span>
          </span>
        </label>
      </fieldset>

      <button
        type="submit"
        style={{
          ...styles.primaryButton,

          opacity: canSubmit
            ? 1
            : 0.5,

          pointerEvents: canSubmit
            ? 'auto'
            : 'none',
        }}
        disabled={!canSubmit}
      >
        {isSubmitting
          ? 'Creating…'
          : 'Create Event'}
      </button>
    </form>
    </div>
  );
}

const styles = {
  form: {
    width: '100%',
    maxWidth: 980,
    margin: '32px auto 0',
    padding: '0 20px 180px',
    boxSizing: 'border-box',
    overflowX: 'hidden',
    fontFamily:
      'Helvetica, Arial, sans-serif',
  },

  heading: {
    textAlign: 'center',
    marginBottom: 22,
  },

  input: {
    display: 'block',
    width: '100%',
    minWidth: 0,
    maxWidth: '100%',
    boxSizing: 'border-box',
    padding: '14px 16px',
    borderRadius: 5,
    border: '1px solid #ccc',
    marginBottom: 14,
    fontSize: 16,
    outline: 'none',
    background: '#fff',
    color: '#000',
  },

  hint: {
    marginTop: -6,
    marginBottom: 12,
    fontSize: 12,
    color: '#333',
  },

  row: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    flexWrap: 'wrap',
  },

  label: {
    fontSize: 14,
    color: '#000',
  },

  preview: {
    width: '100%',
    maxHeight: 260,
    objectFit: 'cover',
    borderRadius: 6,
    marginTop: 12,
  },

  primaryButton: {
    marginTop: 18,
    width: 280,
    maxWidth: '100%',
    height: 48,
    borderRadius: '10px',
    border: '1px solid #000',
    background: '#000',
    color: '#fff',
    cursor: 'pointer',
    display: 'block',
    marginLeft: 'auto',
    marginRight: 'auto',
    fontSize: 16,
  },
};